import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

/**
 * Format currency values for display
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

/**
 * Format percentage for display
 */
const formatPercent = (value) => {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(1)}%`;
};

// Color constants
const COLORS = {
    managedModel: '#0d6efd',      // Bootstrap primary blue
    managedNonModel: '#6f42c1',   // Bootstrap purple
    restricted: '#dc3545',         // Bootstrap danger red
    reservedCash: '#198754',       // Bootstrap success green
    excessCash: '#20c997'          // Bootstrap teal
};

/**
 * PortfolioSummaryPieChart - Displays Before/After pie charts showing portfolio composition
 * with Cash broken into Reserved Cash and Excess Cash
 * 
 * Props:
 *   securityAllocations - Array of security allocation objects from the rebalancer
 *   accountAllocations - Array of account allocation objects (to get total cash and reserve targets)
 *   trades - Array of trade objects (to calculate after cash values)
 */
const PortfolioSummaryPieChart = React.memo(({ securityAllocations = [], accountAllocations = [], trades = [], isLoading = false, ignoreMargin = false }) => {
   
    // Build a map of net trade impacts by account
    // SELL trades bring cash IN, BUY trades take cash OUT
    const netTradeImpactByAccount = {};
    (trades || []).forEach(trade => {
        const accountNumber = trade.account_number || trade.accountNumber;
        const action = (trade.action || trade.Action || '').toUpperCase();
        // Use abs value so both backend unsigned trade_value and live signed trade_value work
        const tradeValue = Math.abs(trade.trade_value || trade.value || 0);
        
        if (accountNumber) {
            const cashImpact = action === 'SELL' ? tradeValue : (action === 'BUY' ? -tradeValue : 0);
            netTradeImpactByAccount[accountNumber] = (netTradeImpactByAccount[accountNumber] || 0) + cashImpact;
        }
    });

    // Calculate BEFORE values for each category
    let beforeManagedModel = 0;
    let beforeManagedNonModel = 0;
    let beforeRestricted = 0;

    // Calculate AFTER values for each category
    let afterManagedModel = 0;
    let afterManagedNonModel = 0;
    let afterRestricted = 0;

    securityAllocations.forEach(item => {
        const beforeValue = item.beforeValue || item.before_value || 0;
        const afterValue = item.afterValue || item.after_value || 0;
        const name = item.name || '';

        if (name.startsWith('RESTRICTED ')) {
            beforeRestricted += beforeValue;
            afterRestricted += afterValue;
        } else if (name === 'Other Holdings') {
            beforeManagedNonModel += beforeValue;
            afterManagedNonModel += afterValue;
        } else if (name !== 'Excess Cash') {
            beforeManagedModel += beforeValue;
            afterManagedModel += afterValue;
        }
    });

    // Calculate cash values from account allocations.
    // Each account's cash is floored at 0 before summing so that a single margin
    // account with deeply negative net cash cannot wipe out positive cash balances
    // from other accounts in the pie total.
    let totalCashCurrentForPie = 0;
    let totalCashAfterForPie = 0;
    let beforeExcessCash = 0;
    let beforeReservedCash = 0;
    let afterExcessCash = 0;
    let afterReservedCash = 0;

    accountAllocations.forEach(account => {
        const accountNumber = account.accountNumber || account.account_number;
        const rawCash = account.cashCurrent || account.cash_current || 0;
        const marginUsed = account.marginCashUsed || account.margin_cash_used || 0;
        // When ignoring margin, add back the margin debit to get gross (unlevered) cash.
        // When counting margin, use net cash but floor at 0 per-account for pie purposes —
        // a margin debit is a liability, not a reduction in asset value on the pie chart.
        const cashCurrent = Math.max(0, ignoreMargin ? rawCash + marginUsed : rawCash);
        const cashReserveCenter = account.cashReserveCenter || account.cash_reserve_center || 0;

        const tradeImpact = netTradeImpactByAccount[accountNumber] || 0;
        const cashAfterTrades = Math.max(0, (ignoreMargin ? rawCash + marginUsed : rawCash) + tradeImpact);

        totalCashCurrentForPie += cashCurrent;
        totalCashAfterForPie += cashAfterTrades;

        // Split cash into reserved vs excess per-account
        beforeReservedCash += Math.min(cashCurrent, cashReserveCenter);
        beforeExcessCash += Math.max(0, cashCurrent - cashReserveCenter);
        afterReservedCash += Math.min(cashAfterTrades, cashReserveCenter);
        afterExcessCash += Math.max(0, cashAfterTrades - cashReserveCenter);
    });

    // Calculate totals using the per-account-floored cash sums
    const beforeTotal = beforeManagedModel + beforeManagedNonModel + beforeRestricted + totalCashCurrentForPie;
    const afterTotal = afterManagedModel + afterManagedNonModel + afterRestricted + totalCashAfterForPie;

    // Build pie data helper function
    const buildPieData = (managedModel, managedNonModel, restricted, reservedCash, excessCash, total) => {
        const data = [];

        if (managedModel > 0) {
            data.push({
                name: 'Managed Model',
                value: managedModel,
                percentage: total > 0 ? (managedModel / total) * 100 : 0,
                color: COLORS.managedModel
            });
        }

        if (managedNonModel > 0) {
            data.push({
                name: 'Non-Model',
                value: managedNonModel,
                percentage: total > 0 ? (managedNonModel / total) * 100 : 0,
                color: COLORS.managedNonModel
            });
        }

        if (restricted > 0) {
            data.push({
                name: 'Restricted',
                value: restricted,
                percentage: total > 0 ? (restricted / total) * 100 : 0,
                color: COLORS.restricted
            });
        }

        if (reservedCash > 0) {
            data.push({
                name: 'Reserved Cash',
                value: reservedCash,
                percentage: total > 0 ? (reservedCash / total) * 100 : 0,
                color: COLORS.reservedCash
            });
        }

        if (excessCash > 0) {
            data.push({
                name: 'Excess Cash',
                value: excessCash,
                percentage: total > 0 ? (excessCash / total) * 100 : 0,
                color: COLORS.excessCash
            });
        }

        return data;
    };

    const beforePieData = buildPieData(
        beforeManagedModel, beforeManagedNonModel, beforeRestricted,
        beforeReservedCash, beforeExcessCash, beforeTotal
    );

    const afterPieData = buildPieData(
        afterManagedModel, afterManagedNonModel, afterRestricted,
        afterReservedCash, afterExcessCash, afterTotal
    );

    // Build restricted asset details for hover tooltip
    // Groups by symbol across accounts using securityAllocations + accountAllocations
    const restrictedDetails = useMemo(() => {
        const details = [];
        // Walk account allocations to get per-account restricted holdings
        (accountAllocations || []).forEach(account => {
            const accountName = account.accountName || account.account_name || '';
            const accountNumber = account.accountNumber || account.account_number || '';
            (account.securities || []).forEach(sec => {
                const name = sec.name || '';
                if (name.startsWith('RESTRICTED ')) {
                    const symbol = name.replace('RESTRICTED ', '');
                    const value = sec.before?.value ?? sec.beforeValue ?? 0;
                    details.push({ symbol, accountName, accountNumber, value });
                }
            });
        });
        return details;
    }, [accountAllocations]);

    // Build non-model asset details for hover tooltip
    // The backend attaches a 'details' array to the "Other Holdings" entry
    // with individual symbol/account/value breakdowns
    const nonModelDetails = useMemo(() => {
        const otherHoldings = (securityAllocations || []).find(sec => sec.name === 'Other Holdings');
        if (otherHoldings && Array.isArray(otherHoldings.details) && otherHoldings.details.length > 0) {
            return otherHoldings.details;
        }
        return [];
    }, [securityAllocations]);

    // Don't render if no data
    if (beforePieData.length === 0 && afterPieData.length === 0) {
        return null;
    }

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white border rounded p-2 shadow-sm" style={{ zIndex: 1000 }}>
                    <p className="mb-0 fw-bold" style={{ color: data.color }}>{data.name}</p>
                    <p className="mb-0 small">{formatCurrency(data.value)}</p>
                    <p className="mb-0 small text-muted">{formatPercent(data.percentage)}</p>
                </div>
            );
        }
        return null;
    };

    // Compact legend component - shows to the right of pie
    const CompactLegend = ({ data, total }) => {
        const [hoveredRestricted, setHoveredRestricted] = useState(false);
        const [hoveredNonModel, setHoveredNonModel] = useState(false);

        return (
            <div style={{ fontSize: '0.65rem' }}>
                {data.map((item, index) => {
                    const isRestricted = item.name === 'Restricted';
                    const isNonModel = item.name === 'Non-Model';
                    const hasHover = (isRestricted && restrictedDetails.length > 0) || (isNonModel && nonModelDetails.length > 0);
                    return (
                        <div
                            key={index}
                            className="d-flex align-items-center justify-content-between"
                            style={{
                                marginBottom: '2px',
                                position: hasHover ? 'relative' : undefined,
                                cursor: hasHover ? 'pointer' : undefined
                            }}
                            onMouseEnter={
                                isRestricted ? () => setHoveredRestricted(true) :
                                isNonModel ? () => setHoveredNonModel(true) : undefined
                            }
                            onMouseLeave={
                                isRestricted ? () => setHoveredRestricted(false) :
                                isNonModel ? () => setHoveredNonModel(false) : undefined
                            }
                        >
                            <div className="d-flex align-items-center">
                                <span
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: item.color,
                                        borderRadius: '2px',
                                        display: 'inline-block',
                                        marginRight: '4px',
                                        flexShrink: 0
                                    }}
                                />
                                <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>
                            </div>
                            <div className="text-end ms-2" style={{ whiteSpace: 'nowrap' }}>
                                <span className="fw-bold">{formatPercent(item.percentage)}</span>
                                <span className="text-muted ms-1">({formatCurrency(item.value)})</span>
                            </div>
                            {/* Restricted hover tooltip */}
                            {isRestricted && hoveredRestricted && restrictedDetails.length > 0 && (
                                <div
                                    className="bg-white border rounded shadow-sm p-2"
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1050,
                                        minWidth: '220px',
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <div className="fw-bold mb-1" style={{ color: COLORS.restricted }}>
                                        Restricted Assets
                                    </div>
                                    {restrictedDetails.map((d, i) => (
                                        <div key={i} className="d-flex justify-content-between gap-2" style={{ marginBottom: '1px' }}>
                                            <span>
                                                <span className="fw-bold">{d.symbol}</span>
                                                <span className="text-muted ms-1">— {d.accountName || d.accountNumber}</span>
                                            </span>
                                            <span className="text-end">{formatCurrency(d.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {/* Non-Model hover tooltip */}
                            {isNonModel && hoveredNonModel && nonModelDetails.length > 0 && (
                                <div
                                    className="bg-white border rounded shadow-sm p-2"
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1050,
                                        minWidth: '220px',
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <div className="fw-bold mb-1" style={{ color: COLORS.managedNonModel }}>
                                        Non-Model Assets
                                    </div>
                                    {nonModelDetails.map((d, i) => (
                                        <div key={i} className="d-flex justify-content-between gap-2" style={{ marginBottom: '1px' }}>
                                            <span>
                                                <span className="fw-bold">{d.symbol}</span>
                                                <span className="text-muted ms-1">— {d.accountName || d.accountNumber}</span>
                                            </span>
                                            <span className="text-end">{formatCurrency(d.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
                <hr className="my-1" />
                <div className="d-flex justify-content-between">
                    <span className="text-muted">Total:</span>
                    <span className="fw-bold">{formatCurrency(total)}</span>
                </div>
            </div>
        );
    };

    // Single pie chart with legend to the right
    const PieWithLegend = ({ data, total, label }) => (
        <div>
            <div className="fw-bold mb-1 text-center" style={{ fontSize: '0.8rem' }}>{label}</div>
            <div className="d-flex align-items-center">
                <div style={{ width: '100px', flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height={100}>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={25}
                                outerRadius={45}
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <CompactLegend data={data} total={total} />
                </div>
            </div>
        </div>
    );

    return (
        <div className="card h-100">
            <div className="card-header bg-light py-2 px-3">
                <h6 className="mb-0">
                    <i className="bi bi-pie-chart-fill me-2"></i>
                    Portfolio Summary
                </h6>
            </div>
            {isLoading ? (
                <div className="card-body p-2 d-flex justify-content-center align-items-center" style={{ minHeight: '150px' }}>
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>Loading portfolio data...</span>
                </div>
            ) : (
            <div className="card-body p-2">
                <div className="row g-2">
                    <div className="col-6">
                        <PieWithLegend data={beforePieData} total={beforeTotal} label="Before" />
                    </div>
                    <div className="col-6">
                        <PieWithLegend data={afterPieData} total={afterTotal} label="After" />
                    </div>
                </div>
            </div>
            )}
        </div>
    );
});

export default PortfolioSummaryPieChart;
